<template>
  <master-layout pageTitle="Create an application">
    <div>
      <div class="chat-toolbar">
        <div class="toolbar-start">
          <VIcon
            @click="() => $router.go(-1)"
            name="arrow-left-line"
            :size="-4"
            class="backBttn cursorPointer"
          ></VIcon>
          <h2 class="prdt-title ion-no-margin">Create an Application</h2>
        </div>
        <div class="toolbar-end">
          <VButton connotation="cta" appearance="filled" label="Save" :disabled="!isFormValid" @click="submitForm" />
        </div>
      </div>
      <div class="gridMargin">
        <div class="bgColor card-wrapper">
          <div class="heading-wrapper">
            <span class="customer-info-title">Application Details</span>
            <span class="sub-title">Enter your application details.</span>
          </div>

          <div class="input-wrapper sectionPadding">
            <div>
              <p class="sub-title ion-no-margin paddingBottom">
                Application Name <span class="required-star">*</span>
              </p>
              <div class="counter-wrapper">
                <VTextField
                  v-model="appName"
                  placeholder="Application Name"
                  :maxlength="30"
                  appearance="ghost"
                  class="vivid-input"
                />
                <div class="custom-counter">{{ appName.length }}/30</div>
              </div>
              <span v-if="appNameError" class="error-text">{{ appNameError }}</span>
            </div>
            <div>
              <p class="sub-title ion-no-margin paddingBottom">
                Application Description
              </p>
              <div class="counter-wrapper">
                <VTextArea
                  v-model="appDescription"
                  placeholder="Application Description"
                  :maxlength="100"
                  :rows="4"
                  appearance="ghost"
                  class="vivid-textarea"
                />
                <div class="custom-counter">{{ appDescription.length }}/100</div>
              </div>
              <span v-if="appDescriptionError" class="error-text">{{ appDescriptionError }}</span>
            </div>
          </div>
        </div>

        <div class="bgColor card-wrapper cust_mrg">
          <div class="heading-wrapper">
            <span class="customer-info-title">Capabilities<span class="required-star">*</span></span>
            <span class="sub-title">
              Turn on capabilities that you want to use with the application.
              You can add and remove capabilities any time.
            </span>
          </div>
          <div class="feature-card-container sectionPadding">
            <div
              class="feature-card-wrapper"
              v-for="(card, index) in products"
              :key="index"
            >
              <FeatureCard
                :title="card.name"
                :description="card.description"
                v-model:enabled="card.enabled"
              />
            </div>
          </div>
          <div v-if="capabilitiesError" class="error-text sectionPadding">
            {{ capabilitiesError }}
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import FeatureCard from "./FeatureCard.vue";
import apiInterceptor from "@/shared/apiInterceptor";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';
export default {
  name: "CreateApp",
  components: { FeatureCard },
  data() {
    return {
      appName: "",
      appDescription: "",
      products: [],
      appNameError: "",
      appDescriptionError: "",
      capabilitiesError: "",
    };
  },
  async ionViewWillEnter() {
    this.resetForm();
    await this.fetchProducts();
  },
  methods: {
    resetForm() {
      this.appName = "";
      this.appDescription = "";
      this.products = [];
      this.appNameError = "";
      this.appDescriptionError = "";
      this.capabilitiesError = "";
    },
    validateForm() {
      // Reset previous errors
      this.appNameError = "";
      this.appDescriptionError = "";
      this.capabilitiesError = "";
      let isValid = true;
      //  Capabilities Validation
      const selected = this.products.filter((c) => c.enabled);
      // Application Name Validation
      if (!this.appName.trim()) {
        const message = "Application Name is required.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (this.appName.length > 30) {
        const message = "Application Name cannot exceed 30 characters.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (this.appName.length < 4) {
        const message = "Application Name must be at least 4 characters long.";
        this.appNameError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      } else if (selected.length === 0) {
        const message = "Select at least one capability.";
        this.capabilitiesError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      }

      //  Description Validation
      if (this.appDescription.length > 100) {
        const message = "Application Description cannot exceed 100 characters.";
        this.appDescriptionError = message;
        useUIStore().updateToast({ open: true, message, type: "danger" });
        isValid = false;
      }

      return isValid;
    },
    async fetchProducts() {
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Loading capabilities...' });
        const productsList = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") + "metadata/products"
        );
        this.products = productsList.products.filter((product)=>product.is_selectable).map((product) => ({
          name: product.name,
          description: product.description,
          id: product.id,
          enabled: false,
        }));
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error?.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }
      const selectedCapabilities = this.products
        .filter((c) => c.enabled)
        .map((c) => c.id);

      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Creating application...' });
        await apiInterceptor.post(
          environment("VUE_APP_APIGEE_API_URL") + "apps",
          {
            app_name: this.appName,
            app_description: this.appDescription,
            api_products: selectedCapabilities,
          }
        );
        useUIStore().updateToast({
          open: true,
          message: "Application created successfully!",
          type: "success",
        });
        this.$router.push({ path: "/developer-portal/applications" });
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error?.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
  },
  computed: {
    isFormValid() {
      const name = this.appName.trim();
      const desc = this.appDescription.trim();
      const selectedCapabilities = this.products.filter((p) => p.enabled);

      if (!name) return false;
      if (name.length < 4) return false;
      if (name.length > 30) return false;
      if (desc.length > 100) return false;
      if (selectedCapabilities.length === 0) return false;

      return true;
    },
  },
  watch: {
    products: {
      deep: true,
      handler(newVal) {
        const selected = newVal.filter((c) => c.enabled);
        if (selected.length > 0) {
          this.capabilitiesError = "";
        }
      },
    },
    appName(newVal) {
      if (!newVal.trim()) {
        this.appNameError = "Application Name is required.";
      } else if (newVal.length < 4) {
        this.appNameError = "Application Name must be at least 4 characters long.";
      } else if (newVal.length > 30) {
        this.appNameError = "Application Name cannot exceed 30 characters.";
      } else {
        this.appNameError = "";
      }
    },
    appDescription(newVal) {
      if (newVal.length > 100) {
        this.appDescriptionError = "Application Description cannot exceed 100 characters.";
      } else {
        this.appDescriptionError = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0px;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}
.toolbar-start {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar-end {
  padding-right: 15px;
}
.heading-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-bottom: 1px solid var(--background-primary);
  padding: 20px;
  padding-bottom: 10px;
}
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.feature-card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: stretch;
  justify-content: space-between; /* default */
}

/* card styling */
.feature-card-wrapper {
  flex: 1 1 calc(33.333% - 20px);
  min-width: 280px;
  display: flex;
}
.error-text {
  color: red;
  font-size: 13px;
  padding-top: 0px;
}
.card-wrapper {
  border-radius: 8px;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.vivid-textarea {
  background: var(--background-primary);
  border-radius: 5px;
  width: 100%;
}
@media (max-width: 768px) {
  .feature-card-wrapper {
    flex: 1 1 100%;
  }
  .input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
}
</style>
